import React from 'react';
import MessageCopyShareActions from './MessageCopyShareActions';
import TeamAssistantRememberMessageAction from './TeamAssistantRememberMessageAction';
import TeamAssistantReplyAnimation from './TeamAssistantReplyAnimation';
import TeamAssistantMarkdownContent from './TeamAssistantMarkdownContent';
import TeamAssistantMessageExtraActions from './TeamAssistantMessageExtraActions';
import { renderTextWithSearchHighlight } from '../../utils/highlightSearchTerm';

/**
 * Renders chat bubbles in document order inside the messages scroll pane.
 */
const TeamAssistantMessageList = ({
  messages,
  streaming,
  renderMessageHeader,
  showCopyShare = true,
  showRememberAction = true,
  highlightQuery = '',
  onAskAgain,
  onRegenerate,
}) => {
  const searchTerm = (highlightQuery || '').trim();

  const lastAssistantIndex = (() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'assistant') return i;
    }
    return -1;
  })();

  const renderAssistantBody = (m, isLiveStream) => {
    const content = m.content || '';
    if (isLiveStream && !(content || '').trim()) {
      return null;
    }
    const body =
      searchTerm && !isLiveStream ? (
        renderTextWithSearchHighlight(content, searchTerm)
      ) : (
        <TeamAssistantMarkdownContent content={content} />
      );

    return (
      <>
        {body}
        {isLiveStream && <TeamAssistantReplyAnimation variant="cursor" />}
      </>
    );
  };

  const renderBubbleBody = (m, index) => {
    if (m.role === 'system') {
      return (
        <div className="team-assistant-system-notice" role="status">
          {renderTextWithSearchHighlight(m.content, searchTerm)}
        </div>
      );
    }

    const isActiveStream =
      streaming && m.role === 'assistant' && index === messages.length - 1;
    const showWaiting = isActiveStream && !(m.content || '').length;
    const canShare = Boolean((m.content || '').trim()) && !isActiveStream;
    const isLastAssistant = index === lastAssistantIndex;

    return (
      <>
        <div className="team-assistant-bubble-body">
          {showWaiting ? (
            <TeamAssistantReplyAnimation variant="waiting" />
          ) : m.role === 'assistant' ? (
            renderAssistantBody(m, isActiveStream)
          ) : (
            renderTextWithSearchHighlight(m.content, searchTerm)
          )}
        </div>
        {showCopyShare && canShare && (
          <div className="team-assistant-bubble-actions-row">
            <MessageCopyShareActions message={m} className="team-assistant-bubble-actions" />
            <TeamAssistantMessageExtraActions
              message={m}
              isLastAssistant={isLastAssistant}
              streaming={streaming}
              onAskAgain={onAskAgain}
              onRegenerate={onRegenerate}
              className="team-assistant-bubble-actions"
            />
            {showRememberAction && (
              <TeamAssistantRememberMessageAction message={m} className="team-assistant-bubble-actions" />
            )}
          </div>
        )}
      </>
    );
  };

  return messages.map((m, index) => (
    <div
      key={m.id}
      className={`team-assistant-bubble ${m.role}${m.role === 'system' ? ' team-assistant-bubble--system' : ''}`}
    >
      {m.role === 'system' ? null : renderMessageHeader(m)}
      {renderBubbleBody(m, index)}
    </div>
  ));
};

export default TeamAssistantMessageList;
